import React, { useState, useEffect, useRef } from 'react';
import { X, Layers, Activity, Globe, HelpCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import { 
  fetchConnections, 
  fetchConnectionActions, 
  createConnection, 
  getConnectionPresets,
  checkConnectionConfig
} from '../../services/connectionService';
import { getConfigComponent } from './apps';

// Animation variants for clean transitions
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.2 }
  },
  exit: { 
    opacity: 0,
    transition: { delay: 0.1, duration: 0.2 }
  }
};

const modalVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { 
      duration: 0.3, 
      ease: [0.16, 1, 0.3, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: 20, 
    scale: 0.98,
    transition: { duration: 0.2, ease: "easeOut" }
  }
};

const optionVariants = {
  initial: { y: 10, opacity: 0 },
  animate: i => ({ 
    y: 0, 
    opacity: 1, 
    transition: { 
      delay: i * 0.1, 
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1]
    }
  }),
  hover: { 
    y: -2, 
    transition: { 
      duration: 0.2, 
      ease: "easeOut" 
    }
  },
  tap: { 
    scale: 0.98,
    transition: { 
      duration: 0.1 
    }
  }
};

const ConnectionSelectionModal = ({ 
  isOpen, 
  onClose,
  onSelectConnection,
  onCreateConnection,
  onSelectConnectionAction = null,
  selectedConnection = null
}) => {
  const [connections, setConnections] = useState([]);
  const [connectionActions, setConnectionActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('connections'); // 'connections', 'config', 'actions', 'create'
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    endpoint: '',
    apiKey: ''
  });
  
  // Add state for current connection being configured
  const [configConnection, setConfigConnection] = useState(null);
  
  // Use ref for the help function to avoid state updates during render
  const showHelpFuncRef = useRef(null);
  
  // Reset step when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('connections');
    }
  }, [isOpen]);
  
  // Fetch connections from the database
  useEffect(() => {
    if (isOpen && step === 'connections') {
      loadConnections();
    }
  }, [isOpen, step]);
  
  // Fetch connection actions if a connection is selected
  useEffect(() => {
    if (selectedConnection && step === 'actions') {
      loadConnectionActions(selectedConnection.id);
    }
  }, [selectedConnection, step]);
  
  // Load connections using our service
  const loadConnections = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchConnections();
      setConnections(data);
    } catch (err) {
      console.error('Error fetching connections:', err);
      setError('Failed to load connections. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Load connection actions using our service
  const loadConnectionActions = async (connectionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchConnectionActions(connectionId);
      setConnectionActions(data);
    } catch (err) {
      console.error('Error fetching connection actions:', err);
      setError('Failed to load actions. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle selecting a connection
  const handleSelectConnection = async (connection) => {
    console.log('Selected connection:', connection);
    
    // Check if the connection is already configured
    setLoading(true);
    const configExists = await checkConnectionConfig(connection.id);
    setLoading(false);
    
    console.log('Connection config exists:', configExists);
    console.log('Connection type:', connection.type);
    console.log('Connection ID:', connection.id);
    
    if (!configExists) {
      // Configuration is needed
      setConfigConnection(connection);
      setStep('config');
      return;
    }
    
    // Connection is already configured
    onSelectConnection(connection);
    
    // If we have an action selection handler, move to the actions step
    if (onSelectConnectionAction) {
      setStep('actions');
    } else {
      onClose();
    }
  };
  
  // Handle configuration saved
  const handleConfigSaved = () => {
    // After config is saved, proceed with the connection we were configuring
    onSelectConnection(configConnection);
    
    // If we have an action selection handler, move to the actions step
    if (onSelectConnectionAction) {
      setStep('actions');
    } else {
      onClose();
    }
  };
  
  // Handle selecting a connection action
  const handleSelectAction = (action) => {
    if (onSelectConnectionAction) {
      onSelectConnectionAction(action);
    }
    onClose();
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission for new connection
  const handleCreateConnection = async () => {
    if (!formData.name || !formData.type) {
      setError('Please provide at least a name and type');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const newConnection = await createConnection(formData);
      
      if (onCreateConnection) {
        onCreateConnection(newConnection);
      }
      
      onClose();
    } catch (err) {
      setError('Failed to create connection. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle moving back a step
  const handleBack = () => {
    if (step === 'actions') {
      setStep('connections');
    } else if (step === 'create') {
      setStep('connections');
    } else {
      onClose();
    }
  };
  
  // Render loading state
  const renderLoading = () => (
    <div className="flex flex-col items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-500 text-sm">Loading {step === 'connections' ? 'connections' : 'actions'}...</p>
    </div>
  );
  
  // Render error state
  const renderError = () => (
    <div className="flex flex-col items-center justify-center py-8">
      <div className="bg-red-50 text-red-600 p-3 rounded-full mb-4">
        <X className="h-6 w-6" />
      </div>
      <p className="text-gray-700 font-medium mb-1">Something went wrong</p>
      <p className="text-gray-500 text-sm mb-4">{error}</p>
      <Button 
        variant="outline" 
        size="sm"
        onClick={step === 'actions' ? () => setStep('connections') : loadConnections}
      >
        Try Again
      </Button>
    </div>
  );
  
  // Get appropriate title for the current step
  const getStepTitle = () => {
    if (step === 'config' && configConnection) {
      // Use the config component's metadata title if available
      const ConfigComponent = getConfigComponent(configConnection.type || configConnection.name, configConnection.id);
      if (ConfigComponent && ConfigComponent.configMetadata && ConfigComponent.configMetadata.title) {
        return ConfigComponent.configMetadata.title;
      }
    }
    
    switch (step) {
      case 'connections':
        return 'Select Connection Type';
      case 'config':
        return 'Configure Connection Settings';
      case 'actions':
        return `Select ${selectedConnection?.name} Action`;
      case 'create':
        return 'Create New Connection';
      default:
        return 'Select Connection';
    }
  };
  
  // Get appropriate icon for a connection
  const getConnectionIcon = (connection) => {
    if (!connection) return <Globe className="h-6 w-6" />;
    
    // Use the connection name to determine which icon to show
    const name = connection.name.toLowerCase();
    if (name.includes('curbside')) {
      return <Layers className="h-6 w-6" />;
    } else if (name.includes('cleancloud')) {
      return <Activity className="h-6 w-6" />;
    } else {
      return <Globe className="h-6 w-6" />;
    }
  };
  
  // Get description for a connection
  const getConnectionDescription = (connection) => {
    if (!connection) return 'Custom connection';
    
    // Use the connection name to determine the description
    const name = connection.name.toLowerCase();
    if (name.includes('curbside')) {
      return 'Connect to Curbside POS system';
    } else if (name.includes('cleancloud')) {
      return 'Connect to CleanCloud platform';
    } else if (name.includes('custom') || name.includes('api')) {
      return 'Use a custom API endpoint';
    } else {
      return `Connect to ${connection.name}`;
    }
  };
  
  // Render connections list
  const renderConnections = () => (
    <div className="space-y-4">
      {connections.map((connection, index) => (
        <motion.div 
          key={connection.id}
          custom={index}
          variants={optionVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
          whileTap="tap"
          onClick={() => handleSelectConnection(connection)}
          className={cn(
            "flex items-center p-5 border-2 rounded-lg cursor-pointer relative group shadow-sm",
            "border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 ease-in-out"
          )}
        >
          <div className="p-3 rounded-full mr-4 bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors duration-300">
            {getConnectionIcon(connection)}
          </div>
          <div>
            <h3 className="font-medium text-gray-900 group-hover:text-blue-800 transition-colors duration-300">
              {connection.name}
            </h3>
            <p className="text-sm text-gray-500 group-hover:text-blue-700/80 transition-colors duration-300">
              {getConnectionDescription(connection)}
            </p>
          </div>
        </motion.div>
      ))}
      
      {/* Create New Connection Option */}
      <motion.div 
        custom={connections.length}
        variants={optionVariants}
        initial="initial"
        animate="animate"
        whileHover="hover"
        whileTap="tap"
        onClick={() => setStep('create')}
        className={cn(
          "flex items-center p-5 border-2 border-dashed rounded-lg cursor-pointer group",
          "border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 ease-in-out"
        )}
      >
        <div className="p-3 rounded-full mr-4 bg-gray-100 text-gray-600 group-hover:bg-gray-200 transition-colors duration-300">
          <X className="h-6 w-6 rotate-45" />
        </div>
        <div>
          <h3 className="font-medium text-gray-900 group-hover:text-gray-800 transition-colors duration-300">
            Create New Connection
          </h3>
          <p className="text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-300">
            Configure a custom connection
          </p>
        </div>
      </motion.div>
    </div>
  );
  
  // Render connection actions list
  const renderActions = () => (
    <div className="space-y-4">
      {connectionActions.map((action, index) => (
        <motion.div 
          key={action.id}
          custom={index}
          variants={optionVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
          whileTap="tap"
          onClick={() => handleSelectAction(action)}
          className={cn(
            "flex items-center p-5 border-2 rounded-lg cursor-pointer relative group shadow-sm",
            "border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 ease-in-out"
          )}
        >
          <div className="p-3 rounded-full mr-4 bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors duration-300">
            <Layers className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900 group-hover:text-blue-800 transition-colors duration-300">
              {action.name}
            </h3>
            <p className="text-sm text-gray-500 group-hover:text-blue-700/80 transition-colors duration-300">
              {action.description}
            </p>
          </div>
        </motion.div>
      ))}
    </div>
  );
  
  // Render configuration form for the selected connection
  const renderConfigForm = () => {
    if (!configConnection) return null;
    
    // Get the appropriate config component for this connection type
    const ConfigComponent = getConfigComponent(configConnection.type || configConnection.name, configConnection.id);
    
    if (!ConfigComponent) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="bg-red-50 text-red-600 p-3 rounded-full mb-4">
            <X className="h-6 w-6" />
          </div>
          <p className="text-gray-700 font-medium mb-1">Configuration Not Available</p>
          <p className="text-gray-500 text-sm mb-4">No configuration component found for this connection type.</p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setStep('connections')}
          >
            Go Back
          </Button>
        </div>
      );
    }
    
    return (
      <ConfigComponent 
        connectionId={configConnection.id}
        connectionName={configConnection.name}
        onConfigSaved={handleConfigSaved}
        onCancel={() => setStep('connections')}
        onShowHelp={(fn) => {
          showHelpFuncRef.current = fn;
        }}
      />
    );
  };
  
  // Render create connection form
  const renderCreateForm = () => (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-800">
          {error}
        </div>
      )}
    
      <div className="space-y-2">
        <label className="block text-sm font-medium">Connection Name</label>
        <input 
          type="text" 
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          className="w-full border border-gray-300 rounded-md p-2 text-sm"
          placeholder="e.g., My API Connection"
        />
      </div>
      
      <div className="space-y-2">
        <label className="block text-sm font-medium">Connection Type</label>
        <select 
          name="type"
          value={formData.type}
          onChange={handleInputChange}
          className="w-full border border-gray-300 rounded-md p-2 text-sm"
        >
          <option value="">Select connection type</option>
          <option value="api">API</option>
          <option value="oauth">OAuth</option>
          <option value="webhook">Webhook</option>
        </select>
      </div>
      
      <div className="space-y-2">
        <label className="block text-sm font-medium">API Endpoint</label>
        <input 
          type="text"
          name="endpoint"
          value={formData.endpoint}
          onChange={handleInputChange}
          className="w-full border border-gray-300 rounded-md p-2 text-sm"
          placeholder="https://api.example.com"
        />
      </div>
      
      <div className="space-y-2">
        <label className="block text-sm font-medium">API Key (Optional)</label>
        <input 
          type="text" 
          name="apiKey"
          value={formData.apiKey}
          onChange={handleInputChange}
          className="w-full border border-gray-300 rounded-md p-2 text-sm"
          placeholder="Enter your API key"
        />
      </div>
      
      <div className="pt-4 flex justify-end space-x-3">
        <Button
          variant="outline"
          onClick={() => setStep('connections')}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateConnection}
          disabled={loading || !formData.name || !formData.type}
        >
          {loading ? 'Creating...' : 'Create Connection'}
        </Button>
      </div>
    </div>
  );
  
  // Determine which content to show based on the current step
  const renderContent = () => {
    if (loading) {
      return renderLoading();
    }
    
    if (error && step !== 'create' && step !== 'config') {
      return renderError();
    }
    
    switch (step) {
      case 'connections':
        return renderConnections();
      case 'config':
        return renderConfigForm();
      case 'actions':
        return renderActions();
      case 'create':
        return renderCreateForm();
      default:
        return null;
    }
  };
  
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9999] flex items-start justify-center pt-[15vh] sm:pt-[20vh]"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <motion.div 
            className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 sm:mx-auto flex flex-col overflow-hidden"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 sm:p-6 border-b border-gray-100 bg-white rounded-t-xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <h2 className="text-lg sm:text-xl font-medium text-gray-900">{getStepTitle()}</h2>
                  
                  {/* Help icon for config step if component has helpIcon metadata */}
                  {step === 'config' && configConnection && (() => {
                    const ConfigComponent = getConfigComponent(configConnection.type || configConnection.name, configConnection.id);
                    if (ConfigComponent?.configMetadata?.helpIcon) {
                      return (
                        <button
                          className="ml-2 p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                          onClick={() => {
                            if (showHelpFuncRef.current) {
                              showHelpFuncRef.current();
                            }
                          }}
                          aria-label="View setup instructions"
                        >
                          <HelpCircle className="w-4 h-4" />
                        </button>
                      );
                    }
                    return null;
                  })()}
                </div>
                <button 
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {step === 'connections' && 'Choose a connection type for your order pathway'}
                {step === 'config' && (() => {
                  // Use config component's custom description if available
                  if (configConnection) {
                    const ConfigComponent = getConfigComponent(configConnection.type || configConnection.name, configConnection.id);
                    if (ConfigComponent?.configMetadata?.description) {
                      return ConfigComponent.configMetadata.description;
                    }
                  }
                  return 'Configure your connection settings';
                })()}
                {step === 'actions' && 'Select an action for this connection'}
                {step === 'create' && 'Configure a new custom connection'}
              </p>
            </div>

            {/* Content */}
            <div className="p-5 sm:p-6 space-y-4">
              {renderContent()}
            </div>

            {/* Footer - only show back button on actions and create steps */}
            {(step === 'actions' || step === 'create') && (
              <motion.div 
                className="p-5 sm:p-6 border-t border-gray-100 bg-gray-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.2 } }}
              >
                <div className="flex justify-start">
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                  >
                    Back
                  </Button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConnectionSelectionModal; 